import type { Cents } from '../../shared/money/cents.js'

/**
 * Normalização de unidade e preço por unidade base.
 *
 * *"Sem isso, comparar preço é ilusão"* — `Docs/02-Dominio/Product.md`.
 *
 * ## O problema que a nota não viu: centavo inteiro DESTRÓI a comparação
 *
 * `Docs/02-Dominio/ProductPriceHistory.md` declara
 * `normalizedPriceCents: number` e dá o próprio exemplo que o quebra:
 * *"R$ 0,00798 por ml contra R$ 0,00699 por ml"*. Os dois são **menos de um
 * centavo**. Guardados como centavo inteiro — que é a regra desta casa desde a
 * decisão 9 —, os dois viram `1`, e a comparação que justifica o projeto
 * inteiro responde "empate".
 *
 * A saída **não** é passar a usar float para dinheiro. É mudar a **unidade**: o
 * preço por unidade base é guardado em **milicentavos**, inteiro. R$ 3,99 por
 * 500 ml = 399 centavos / 500 = 0,798 c/ml = **798 mc/ml**. R$ 6,99 por 1 L =
 * 699 / 1000 = **699 mc/ml**. O segundo é mais barato, e a diferença sobrevive
 * como número inteiro — que é o item 3 da definição de pronto da fase.
 *
 * **Milicentavo NÃO é `Cents`, e o tipo diz isso.** Somar um com o outro é o
 * erro que este apelido existe para tornar visível: `MilliCents` é um `number`
 * com nome próprio, e nenhuma função de `@shared/money` o aceita.
 *
 * ## Quatro unidades base, e não nove
 *
 * `ml`, `g`, `un` e `cm`. Litro vira mililitro, quilo vira grama, metro vira
 * centímetro, e caixa e pacote viram unidade — porque `cx` e `pct` **não dizem
 * tamanho**: uma caixa de 12 e uma de 24 são a mesma palavra. Quem sabe o
 * tamanho é `packQuantity`, e é ele que multiplica.
 *
 * **Comprimento entrou porque existe prateleira inteira medida assim**: fio
 * dental, papel higiênico, papel-alumínio, filme plástico. Sem ele, comparar um
 * fio dental de 50 m com um de 100 m dependia de o nome trazer o número — e a
 * comparação por embalagem responde "o de 50 é mais barato", que é verdade e
 * inútil.
 *
 * A base é o **centímetro**, e não o metro, pela mesma razão de `ml` e `g`
 * serem as bases dos seus pares: a unidade pequena é a que não perde resolução
 * ao converter. A LEITURA é por metro — ver `PARA_LER`.
 *
 * Comparar `ml` com `g` **não é possível**, e a função recusa em vez de
 * inventar: densidade não é dado que este app tenha. Dois produtos em bases
 * diferentes simplesmente não se comparam, e a tela precisa saber disso.
 */

export type Unit = 'ml' | 'l' | 'g' | 'kg' | 'cm' | 'm' | 'un' | 'cx' | 'pct'
export type BaseUnit = 'ml' | 'g' | 'cm' | 'un'

/**
 * A lista, em ordem de tela, e a **única** que existe.
 *
 * Ela morava copiada em quatro lugares — as duas telas de produto, o validador
 * do adapter e o tipo. Acrescentar `cm`/`m` em três e esquecer o quarto daria
 * um `<select>` que oferece a unidade e um adapter que a recusa na leitura, com
 * o produto voltando do banco com `unit: null` e sem erro nenhum.
 */
export const UNITS: readonly Unit[] = ['ml', 'l', 'g', 'kg', 'cm', 'm', 'un', 'cx', 'pct']

/**
 * **Milicentavos: um milésimo de centavo.** Existe só para o preço por unidade
 * base, onde o centavo inteiro perde a informação. Ver o cabeçalho.
 */
export type MilliCents = number

/** Quantas unidades base cabem em uma da unidade declarada. */
const EM_BASE: Record<Unit, { base: BaseUnit; fator: number }> = {
  ml: { base: 'ml', fator: 1 },
  l: { base: 'ml', fator: 1000 },
  g: { base: 'g', fator: 1 },
  kg: { base: 'g', fator: 1000 },
  cm: { base: 'cm', fator: 1 },
  m: { base: 'cm', fator: 100 },
  un: { base: 'un', fator: 1 },
  // Caixa e pacote não dizem tamanho — quem diz é `packQuantity`. Ver o
  // cabeçalho.
  cx: { base: 'un', fator: 1 },
  pct: { base: 'un', fator: 1 },
}

export function baseUnitOf(unit: Unit): BaseUnit {
  return EM_BASE[unit].base
}

/**
 * Quantas unidades base há numa embalagem.
 *
 * `size` é o tamanho de **uma** unidade e `packQuantity` quantas vêm no pacote:
 * um fardo de 6 garrafas de 500 ml são 3.000 ml. Multiplicar os dois é o que
 * faz o fardo comparar com a garrafa avulsa.
 */
export function baseQuantity(size: number, unit: Unit, packQuantity: number): number {
  return size * EM_BASE[unit].fator * packQuantity
}

/** Não dá para dividir por zero, e tamanho negativo não é tamanho. */
export class InvalidSizeError extends Error {}

/**
 * O preço por unidade base, em **milicentavos**.
 *
 * `totalCents` é o que se pagou pela embalagem inteira — com sinal ou sem,
 * tanto faz: o módulo entra aqui, porque preço de produto não tem lado. Quem
 * carrega sinal é a transação.
 *
 * **Arredonda ao milicentavo mais próximo**, e o resto some. É perda real e é
 * aceitável: um milésimo de centavo por mililitro é R$ 0,01 a cada dez litros.
 * O que **não** seria aceitável é o arredondamento do centavo, que apaga a
 * diferença inteira entre dois produtos.
 */
export function pricePerBaseUnit(
  totalCents: Cents,
  size: number,
  unit: Unit,
  packQuantity: number,
): MilliCents {
  if (!Number.isFinite(size) || size <= 0) {
    throw new InvalidSizeError(`tamanho precisa ser positivo, recebi ${String(size)}`)
  }
  if (!Number.isInteger(packQuantity) || packQuantity <= 0) {
    throw new InvalidSizeError(
      `quantidade do pacote precisa ser inteiro positivo, recebi ${String(packQuantity)}`,
    )
  }

  const base = baseQuantity(size, unit, packQuantity)
  return Math.round((Math.abs(totalCents) * 1000) / base)
}

/**
 * A unidade em que o preço é **lido**, e o fator para chegar nela.
 *
 * Guardar em `ml` e `g` está certo — é onde a diferença sobrevive como inteiro.
 * Mas ninguém compara refrigerante por mililitro: a pergunta na gôndola é
 * "quanto o litro". `PARA_LER` é a tradução entre as duas.
 */
const PARA_LER: Record<BaseUnit, { rotulo: string; fator: number }> = {
  ml: { rotulo: 'L', fator: 1000 },
  g: { rotulo: 'kg', fator: 1000 },
  // Metro, e não centímetro: "R$ 0,26 / m" é o número que se compara na
  // gôndola; "R$ 0,0026 / cm" é o mesmo fato ilegível.
  cm: { rotulo: 'm', fator: 100 },
  un: { rotulo: 'un', fator: 1 },
}

/**
 * `798` mc/ml → `'R$ 7,98 / L'`. **A única formatação de preço por unidade do
 * app.**
 *
 * Existia uma segunda, `formatPerBaseUnit`, que dava `'R$ 0,00798 / ml'`. Ela
 * está **certa** e era ilegível: o número que decide a compra aparecia com três
 * zeros na frente, em toda tela. Duas formatações também significavam duas
 * respostas para a mesma pergunta dependendo da tela — a lista dizia
 * `R$ 0,00798 / ml` e a comparação de tamanhos dizia `R$ 7,98 / L`, sobre o
 * mesmo produto. Ver a decisão 191.
 *
 * Duas casas, porque é assim que preço se lê. **Mas cai para cinco quando duas
 * dariam `R$ 0,00`**: um item barato por unidade não pode virar "de graça" numa
 * tela cujo objetivo inteiro é comparar preço.
 */
export function formatPerComparableUnit(milliCents: MilliCents, base: BaseUnit): string {
  const { rotulo, fator } = PARA_LER[base]
  const reais = (milliCents * fator) / 100_000
  const casas = reais < 0.01 && reais > 0 ? 5 : 2
  return `R$ ${reais.toFixed(casas).replace('.', ',')} / ${rotulo}`
}

/**
 * Se dois produtos se comparam.
 *
 * **Recusa em vez de inventar.** `ml` e `g` são bases diferentes, e converter
 * exigiria densidade — dado que este app não tem e não vai ter. Uma comparação
 * silenciosa entre eles produziria "o shampoo é mais barato que o arroz", que é
 * uma frase sem significado.
 */
export function comparable(a: Unit, b: Unit): boolean {
  return baseUnitOf(a) === baseUnitOf(b)
}

/**
 * O tamanho da embalagem como se lê: `"500 ml"`, `"1 l × 4"`.
 *
 * `null` quando falta `size` ou `unit` — o estado normal de produto recém
 * criado. Devolver `null` em vez de `"sem tamanho"` deixa cada tela decidir se
 * mostra a ausência ou se some com a linha: a comparação de família **precisa**
 * dizer "sem tamanho" (é o motivo de a variação não entrar no ranking), e uma
 * sugestão de busca não precisa gastar largura com isso.
 *
 * `packQuantity` só aparece a partir de dois, com `×`: "1 l × 4" é o fardo, e
 * "1 l × 1" seria ruído. `size` é o tamanho de UMA unidade — ver `baseQuantity`.
 */
export function formatSize(
  size: number | null,
  unit: Unit | null,
  packQuantity: number,
): string | null {
  if (size === null || unit === null) return null
  return `${String(size)} ${unit}${packQuantity > 1 ? ` × ${String(packQuantity)}` : ''}`
}
